/**
 * A host-managed (external) indicator as the legend row EdgeChart draws for it. The host
 * owns the klinecharts instance; the row only carries what the legend shows and which of
 * its buttons the entry wires.
 */
import type { ActiveIndicator } from '../components/IndicatorLegend'
import { CANDLE_PANE_ID, type ExternalIndicatorEntry } from '../types/chart'

/** The entry declares figures the native drawer's Style tab can edit. */
export function externalHasStyles(e: ExternalIndicatorEntry): boolean {
  return !!(e.figures?.length && e.onStylesChange)
}

/** The entry declares inputs the native drawer's Inputs tab can edit (a script's input.*). */
export function externalHasInputs(e: ExternalIndicatorEntry): boolean {
  return !!(e.params?.length && e.onCalcParamsChange)
}

/**
 * The native settings drawer has something to edit for this entry: plot styles, inputs,
 * or both. A script that only draws lines / labels / boxes or colors bars has inputs and
 * no plots, and still opens the drawer (on its Inputs). The legend gear, the open
 * handler and the drawer mount all gate on this one check.
 */
export function externalOpensNativeDrawer(e: ExternalIndicatorEntry): boolean {
  return externalHasStyles(e) || externalHasInputs(e)
}

export function externalLegendRow(e: ExternalIndicatorEntry): ActiveIndicator {
  return {
    key: e.key,
    name: e.instanceName,
    instanceName: e.instanceName,
    label: e.label,
    paneId: e.paneId,
    paneType: e.paneId === CANDLE_PANE_ID ? 'main' : 'sub',
    visible: e.visible,
    styles: e.styles,
    // The move arrow only renders when the row carries a placement.
    placement: e.onMovePane ? e.placement : undefined,
    // The focus (pin-to-price) button only renders when the row wires
    // onToggleFocus; isFocused drives its active state.
    focusable: !!e.onToggleFocus,
    isFocused: e.isFocused,
    // drawn on the candles while its pane stays below: the move arrow points down
    ...(e.onCandles ? { onCandles: true } : {}),
    // Gear works via a custom handler OR the native settings drawer (styles
    // and/or inputs to edit); hidden when the entry has nothing to edit.
    hideSettings: !e.onOpenSettings && !externalOpensNativeDrawer(e),
    hideRemove: !e.onRemove,
    ...(e.removeTitle ? { removeTitle: e.removeTitle } : {}),
    locatable: !!e.onLocateSource,
    lane: e.lane,
    valueFormats: e.valueFormats,
    // a figure colored per bar (a script plot's series color): the legend value's color
    ...(e.figureColorAt ? { figureColorAt: e.figureColorAt } : {}),
    // a host computation in flight: the spinner after the name
    ...(e.loading ? { loading: true } : {}),
  }
}
